import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';

/** Problem type the API returns when the password is right but the email isn't verified yet. */
const EMAIL_UNVERIFIED = 'urn:regigov:problem:email-unverified';

export function LoginPage() {
  const { login, config } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [unverified, setUnverified] = useState(false);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setUnverified(false);
    try {
      await login(username, password);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Unable to reach the RegiGov API');
      setUnverified(err instanceof ApiError && err.problem.type === EMAIL_UNVERIFIED);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login">
      <section className="login__card" aria-labelledby="login-title">
        <p className="brand">RegiGov</p>
        <h1 id="login-title">CER Device Register</h1>
        <p className="muted">Proof-of-concept sandbox. Synthetic data only.</p>

        {config?.provider === 'entra' ? (
          <p className="notice">
            This environment is configured for Microsoft Entra External ID sign-in. Sign in from your organisation portal to continue.
          </p>
        ) : (
          <form onSubmit={onSubmit} className="form">
            <label>
              Username
              <input name="username" autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} required />
            </label>
            <label>
              Password
              <input
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </label>
            {error && (
              <p role="alert" className="error">
                {error}
              </p>
            )}
            {unverified && (
              <p>
                <Link to="/verify-email">Send me a new verification link</Link>
              </p>
            )}
            <button type="submit" disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
            {config?.provider === 'local' && config.passwordReset && (
              <p>
                <Link to="/forgot-password">Forgot my password?</Link>
              </p>
            )}
            {config?.provider === 'local' && config.selfRegistration && (
              <p>
                New to RegiGov? <Link to="/register">Create an account</Link>
              </p>
            )}
          </form>
        )}
      </section>
    </main>
  );
}
